import type { ComponentType } from 'react';
import { Activity } from '@/components/icons/glyphs/Activity'; import { BarChart3 } from '@/components/icons/glyphs/BarChart3'; import { Bot } from '@/components/icons/glyphs/Bot'; import { Building2 } from '@/components/icons/glyphs/Building2'; import { CircleDollarSign } from '@/components/icons/glyphs/CircleDollarSign'; import { ClipboardList } from '@/components/icons/glyphs/ClipboardList'; import { FileClock } from '@/components/icons/glyphs/FileClock'; import { Gauge } from '@/components/icons/glyphs/Gauge'; import { Home } from '@/components/icons/glyphs/Home'; import { Inbox } from '@/components/icons/glyphs/Inbox'; import { KeyRound } from '@/components/icons/glyphs/KeyRound'; import { Landmark } from '@/components/icons/glyphs/Landmark'; import { Layers } from '@/components/icons/glyphs/Layers'; import { Mail } from '@/components/icons/glyphs/Mail'; import { MailPlus } from '@/components/icons/glyphs/MailPlus'; import { MessageSquareText } from '@/components/icons/glyphs/MessageSquareText'; import { MessagesSquare } from '@/components/icons/glyphs/MessagesSquare'; import { PlayCircle } from '@/components/icons/glyphs/PlayCircle'; import { Radio } from '@/components/icons/glyphs/Radio'; import { ReceiptText } from '@/components/icons/glyphs/ReceiptText'; import { Sparkles } from '@/components/icons/glyphs/Sparkles'; import { Users } from '@/components/icons/glyphs/Users'; import { Wallet } from '@/components/icons/glyphs/Wallet';



/**
 * Estructura de navegación del admin (barra lateral y paleta ⌘K comparten esta fuente: sin entradas duplicadas).
 *
 * - Platform admin: grupo PLATAFORMA (arriba) + grupo TENANT · <nombre> (abajo).
 * - Usuario de tenant (owner/admin/editor/viewer): solo el grupo TENANT, con Inicio y Tenants.
 *
 * `alsoActive` marca el ítem como activo en rutas que ya no tienen entrada propia pero siguen funcionando
 * (`/costs`, `/platform/budgets`, `/tenants/:id`…): «pertenecen» a ese ítem.
 */
export type NavTo =
  | '/'
  | '/platform'
  | '/platform/tenants'
  | '/platform/users'
  | '/platform/costs'
  | '/platform/prices'
  | '/platform/budgets'
  | '/platform/operations'
  | '/platform/audit'
  | '/platform/services'
  | '/platform/fidelizador'
  | '/platform/plans'
  | '/platform/subscriptions'
  | '/platform/ai'
  | '/tenants'
  | '/agents'
  | '/channels'
  | '/fidelizador'
  | '/campaigns'
  | '/experience-feedback'
  | '/conversations'
  | '/inbox'
  | '/requests'
  | '/analytics'
  | '/costs'
  | '/demos';

export interface NavItem {
  to: NavTo;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** El enlace conserva `?tenant=` (rutas por tenant). */
  tenantScoped?: boolean;
  /** Rutas (prefijo) que también marcan este ítem como activo. */
  alsoActive?: readonly string[];
  /** Solo se muestra si este interruptor `fidelizador.*` del tenant actual está contratado y encendido. */
  requiresFeature?: string;
  /** Palabras extra para la paleta ⌘K. */
  keywords?: readonly string[];
  /** Texto en la paleta ⌘K cuando el rótulo se repetiría en otro grupo (barra lateral y paleta comparten la fuente). */
  paletteLabel?: string;
}

/** Grupo PLATAFORMA: solo lo ve un platform_admin (la API y el router vuelven a exigirlo). */
export const PLATFORM_NAV: readonly NavItem[] = [
  { to: '/platform', label: 'Resumen', icon: Gauge, keywords: ['inicio', 'home'] },
  { to: '/platform/tenants', label: 'Tenants', icon: Landmark, alsoActive: ['/tenants'], keywords: ['clientes'] },
  { to: '/platform/users', label: 'Usuarios', icon: Users },
  {
    to: '/platform/costs',
    label: 'Costos y margen',
    icon: ReceiptText,
    alsoActive: ['/platform/budgets'],
    keywords: ['facturacion', 'gasto'],
  },
  { to: '/platform/prices', label: 'Precios', icon: KeyRound, keywords: ['tarifas', 'modelos'] },
  { to: '/platform/operations', label: 'Operación', icon: Activity, keywords: ['colas', 'workers', 'webhooks'] },
  { to: '/platform/audit', label: 'Auditoría', icon: FileClock, keywords: ['bitacora'] },
  {
    to: '/platform/services',
    label: 'Servicios',
    icon: Layers,
    keywords: ['catalogo', 'contratar', 'funciones', 'ia'],
  },
  {
    to: '/platform/fidelizador',
    label: 'Fidelizador',
    paletteLabel: 'Fidelizador (plataforma)',
    icon: Mail,
    keywords: ['correo', 'email', 'envios', 'credenciales', 'compartida', 'topes'],
  },
  {
    to: '/platform/plans',
    label: 'Planes',
    icon: ClipboardList,
    keywords: ['cuota', 'precio', 'margen', 'suscripcion', 'tarifa'],
  },
  {
    to: '/platform/subscriptions',
    label: 'Suscripciones',
    icon: Wallet,
    keywords: ['autoservicio', 'registro', 'pago', 'activar', 'contratacion', 'pendiente'],
  },
  { to: '/platform/ai', label: 'IA de plataforma', icon: Sparkles, keywords: ['claves', 'llm'] },
];

/** Grupo TENANT (el tenant actual). «Costos» del tenant vive como «Gasto por conversación» dentro de Analítica. */
export const TENANT_NAV: readonly NavItem[] = [
  { to: '/agents', label: 'Agentes', icon: Bot, tenantScoped: true },
  { to: '/channels', label: 'Canales', icon: Radio, tenantScoped: true, keywords: ['widget', 'whatsapp', 'claves'] },
  {
    to: '/fidelizador',
    label: 'Fidelizador',
    icon: Mail,
    tenantScoped: true,
    keywords: ['correo', 'email', 'envios', 'smtp', 'relay', 'remitente', 'campanas'],
  },
  {
    to: '/campaigns',
    label: 'Campañas',
    icon: MailPlus,
    tenantScoped: true,
    requiresFeature: 'fidelizador.campaigns',
    keywords: ['correo', 'email', 'marketing', 'audiencia', 'envio', 'masivo', 'newsletter', 'resultados'],
  },
  {
    to: '/experience-feedback',
    label: 'Valoraciones',
    icon: MessageSquareText,
    tenantScoped: true,
    requiresFeature: 'fidelizador.post_experience',
    keywords: ['experiencia', 'nota', 'opinion', 'comentarios', 'satisfaccion', 'encuesta', 'postexperiencia'],
  },
  {
    to: '/conversations',
    label: 'Conversaciones',
    icon: MessagesSquare,
    tenantScoped: true,
    keywords: [
      'mensajes',
      'chat',
      'transcripcion',
      'herramientas',
      'tools',
      'tono',
      'flujo',
      'traza',
      'costo',
      'busqueda',
    ],
  },
  {
    to: '/inbox',
    label: 'Bandeja',
    icon: Inbox,
    tenantScoped: true,
    keywords: ['humano', 'persona', 'traspaso', 'handoff', 'responder', 'tomar', 'whatsapp', 'mensajes'],
  },
  {
    to: '/requests',
    label: 'Solicitudes',
    icon: ClipboardList,
    tenantScoped: true,
    keywords: ['gestion', 'cotizacion', 'hora', 'cita', 'taller', 'repuesto', 'reclamo', 'confirmar', 'equipo'],
  },
  {
    to: '/analytics',
    label: 'Analítica',
    icon: BarChart3,
    tenantScoped: true,
    alsoActive: ['/costs'],
    keywords: ['metricas', 'estadisticas', 'leads', 'conversiones', 'costos', 'gasto'],
  },
  {
    to: '/demos',
    label: 'Demos',
    icon: PlayCircle,
    tenantScoped: true,
    keywords: ['probar', 'escenarios', 'prospectos'],
  },
];

/** Solo para quien NO es platform admin (no tiene grupo PLATAFORMA): Inicio y su lista de tenants, antes del resto. */
const MEMBER_NAV_BEFORE: readonly NavItem[] = [
  { to: '/', label: 'Inicio', icon: Home },
  { to: '/tenants', label: 'Tenants', icon: Building2, keywords: ['clientes'] },
];

/** Ítems del grupo TENANT según quién mira. */
export function tenantGroupItems(isPlatformAdmin: boolean): readonly NavItem[] {
  return isPlatformAdmin ? TENANT_NAV : [...MEMBER_NAV_BEFORE, ...TENANT_NAV];
}

/** Acciones que ya no están en el menú pero siguen siendo destinos válidos de la paleta ⌘K. */
export const PALETTE_ONLY_PLATFORM: readonly NavItem[] = [
  {
    to: '/platform/budgets',
    label: 'Ir a Presupuestos',
    icon: CircleDollarSign,
    keywords: ['presupuesto', 'limite', 'alertas', 'umbral'],
  },
];
export const PALETTE_ONLY_TENANT: readonly NavItem[] = [
  {
    to: '/costs',
    label: 'Ir a Gasto por conversación',
    icon: CircleDollarSign,
    tenantScoped: true,
    keywords: ['costos', 'gasto', 'conversacion', 'llm'],
  },
];

/** Todas las rutas a las que apunta algún ítem de menú o acción de la paleta (para el test de rutas existentes y la guarda). */
export const ALL_NAV_TARGETS: readonly NavTo[] = [
  ...new Set<NavTo>(
    [...PLATFORM_NAV, ...tenantGroupItems(false), ...PALETTE_ONLY_PLATFORM, ...PALETTE_ONLY_TENANT].map((i) => i.to),
  ),
];

/** ¿La ruta actual pertenece a este ítem? (`/` y `/platform` solo coinciden exactos: son raíces de otras rutas). */
export function isNavActive(item: NavItem, pathname: string): boolean {
  const norm = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  const under = (base: string) => norm === base || (base !== '/' && norm.startsWith(`${base}/`));
  const extra = (item.alsoActive ?? []).some(under);
  if (item.to === '/' || item.to === '/platform') return norm === item.to || extra;
  return under(item.to) || extra;
}
